import { getDemoStatus } from '@lms/api-client/backend';
import useSWR from 'swr';
import { PageBox } from '@/components/admin/page-box';
import { useSiteAccess } from '@/features/auth/use-auth';
import type { Site } from '@/features/branding/brand-provider';
import { DemoControls } from '@/features/demo/demo-controls';

/**
 * Super-admin-only "Demo mode" card for a single location's settings page.
 * Renders nothing for non-super-admins or non-location sites. Ports
 * features/demo/components/demo-card.tsx: the Next server component awaited the
 * current user and demo status; here the gate comes from `useSiteAccess` and the
 * status loads via SWR (shared with the demo overview page so both revalidate
 * together after a mutation).
 */
export function DemoCard({ site }: { site: Site }) {
  const { access } = useSiteAccess(site.slug);
  const eligible = !!access?.superAdmin && site.siteType === 'location';

  const {
    data: status,
    error,
    mutate,
  } = useSWR(eligible ? ['demo/status'] : null, () => getDemoStatus());

  // Not applicable, still loading, or the demo API is unavailable on this
  // backend — hide the card rather than break the settings page.
  if (!eligible || error || status === undefined) {
    return null;
  }

  const current = status.locations.find((l) => l.id === site.id);

  return (
    <PageBox size="sm">
      <DemoControls
        locationId={site.id}
        current={current}
        autoRefreshEnabled={status.autoRefreshEnabled}
        retentionDays={status.retentionDays}
        onChanged={() => mutate()}
      />
    </PageBox>
  );
}
